/**
 * `spatial-2d` (autorunner) schemas (TDD §3.1). Zod is the boundary contract; the
 * refinements here are the schema-level half of the validator (T8) — a missing REQUIRED
 * kind field is a schema error, never a silent skip that lets a validator false-pass.
 */
import { z } from 'zod';

// The 3Cs profile moved to ../../three-cs (a discriminated union by archetype, design §15).
// This kind consumes the autorunner variant; re-exported here for back-compat.
export { AutorunnerThreeCsSchema as ThreeCsProfileSchema } from '../../three-cs.js';
export type { AutorunnerThreeCs as ThreeCsProfile } from '../../three-cs.js';

export const SEGMENT_KINDS = [
  'gap',
  'fence',
  'platform',
  'reversal',
  'collapse',
  'conveyor',
] as const;

/** Segment kinds that are SOLID GROUND the runner stands/runs on — used for run-up
 *  accumulation and min-width checks. Everything here except `gap` bears the runner;
 *  a `platform` and a `conveyor` are both smooth walkable floor (the conveyor scrolls
 *  the runner along at `speedMultiplier`). `fence`/`reversal`/`collapse` bear ground too
 *  but are obstacles/hazards, so they don't count as clean run-up. */
export const WALKABLE_SEGMENT_KINDS = ['platform', 'conveyor'] as const;
export type SegmentKind = (typeof SEGMENT_KINDS)[number];

/** Entity roles (design §17) — a STRUCTURED classifier the producer sets, so the
 *  previs/build agents render the right marker without regexing the `ref` string.
 *  `prop` is the neutral default (decoration, spawn point, trigger). */
export const ENTITY_ROLES = ['enemy', 'pickup', 'checkpoint', 'hazard', 'prop', 'exit'] as const;
export type EntityRole = (typeof ENTITY_ROLES)[number];

const VertexSchema = z.object({
  id: z.string().min(1),
  t: z.number().finite().min(0).max(1),
  elevation: z.number().finite(),
  hazard: z.string().optional(),
  difficulty: z.number().finite().min(0).max(1).optional(),
});

/** Entity placement (design §17): an instance of a Content item on the track —
 *  enemies, pickups, checkpoints, spawn waves. `ref` is the EXACT Content item
 *  name; extra typed fields ride along (wave size, patrol range, …). */
const SpatialEntitySchema = z
  .object({
    id: z.string().min(1),
    ref: z.string().min(1),
    /** Position along the track (same axis as vertex t). */
    t: z.number().finite().min(0).max(1),
    elevation: z.number().finite().optional(),
    /** What this instance IS — drives the previs marker; never inferred from `ref`. */
    role: z.enum(ENTITY_ROLES).optional(),
    /** Behavior (design §17) — a STRUCTURED, build-agent-consumable spec, not an engine:
     *  hp = hits to defeat (omit ⇒ one-hit / stomp); movement = how it moves;
     *  patrolRange = patrol span (m). Genre-specific extras still ride via passthrough. */
    hp: z.number().finite().positive().optional(),
    movement: z.enum(['static', 'patrol', 'chase']).optional(),
    patrolRange: z.number().finite().positive().optional(),
    /** Seconds for one full patrol sweep (there-and-back) — drives moving-platform
     *  animation in the previs and the sim's move speed. */
    patrolSec: z.number().finite().positive().optional(),
    /** Cyclic on/off timing (design §17) — the SINGLE source both the previs animation
     *  and the game read, so a previs ghost-run matches in-game timing. Models toggling /
     *  vanishing blocks (Mega-Man Yoku), retracting spikes, erupting geysers, etc.
     *  `loopSec` = full period; `activeSec` = the dangerous / toggled window; optional
     *  `telegraphSec` = a warn window before active; optional `phase` (0..1) pins the
     *  offset (absent → the consumer arrival-times it to the runner). */
    cycle: z
      .object({
        loopSec: z.number().finite().positive(),
        activeSec: z.number().finite().positive(),
        telegraphSec: z.number().finite().nonnegative().optional(),
        phase: z.number().finite().min(0).max(1).optional(),
      })
      .optional(),
    /** For role "exit" — a secret exit / alternate goal that routes to another level
     *  (its id/name). A level thus has a normal finish AND optional secret exits — the
     *  "three ways out" branch, without a full route-graph rewrite. */
    exitTo: z.string().optional(),
  })
  .passthrough();

/** A terraced platform (design §17): an overhead one-way platform forming an ALTERNATE
 *  high route above the ground spine — a Mario-style upper ledge you jump onto to
 *  collect a coin row, then drop back. Spans a `t` range at a fixed `height` (m above
 *  the elevation-0 baseline). Reachability is validated against the jump arc, not a
 *  static knob (geometry §4.2 `terrace-*` findings). */
const TerraceSchema = z
  .object({
    id: z.string().min(1),
    /** Track-relative start/end (same axis as vertex t). */
    tStart: z.number().finite().min(0).max(1),
    tEnd: z.number().finite().min(0).max(1),
    /** Height (m) of the platform surface above the elevation-0 baseline. */
    height: z.number().finite().positive(),
  })
  .superRefine((ter, ctx) => {
    if (ter.tEnd <= ter.tStart) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'terrace tEnd must be greater than tStart',
        path: ['tEnd'],
      });
    }
  });

const SegmentSchema = z
  .object({
    fromId: z.string().min(1),
    toId: z.string().min(1),
    kind: z.enum(SEGMENT_KINDS),
    span: z.number().finite().min(0), // edge-to-edge (Q1)
    /** Pit character (only meaningful for `kind: 'gap'`): `shallow` = a one-hop
     *  chasm — a short, recoverable drop the player clears by jumping; `deep` = a
     *  fall-INTO shaft taller than the jump arc, escapable only by wall-kick (the
     *  "open pit you fall into" the design calls for). ABSENT → the consumer
     *  derives it deterministically from `span` vs the 3Cs jump reach (wider than
     *  a clearable hop ⇒ deep) — never by guessing from the level's name. */
    depth: z.enum(['shallow', 'deep']).optional(),
    obstacleHeight: z.number().finite().positive().optional(),
    obstacleWidth: z.number().finite().min(0).optional(),
    requiresSimultaneous: z.boolean().optional(),
    clearWindowS: z.number().finite().positive().optional(),
    collapseSpeed: z.number().finite().positive().optional(),
    /** Belt speed multiplier for `kind: 'conveyor'` — the runner's forward speed is scaled
     *  by this over the segment's span (>1 speeds up, <1 slows). Absent ⇒ 1.5 (a moderate
     *  boost belt), matching the consumer default. */
    speedMultiplier: z.number().finite().positive().optional(),
    // NOTE: no `runwayBefore` — it is validator-DERIVED, never authored (TDD §3.1, R3).
  })
  .superRefine((seg, ctx) => {
    if (seg.kind === 'fence' && seg.obstacleHeight == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'fence requires obstacleHeight',
        path: ['obstacleHeight'],
      });
    }
    if (seg.kind === 'reversal' && seg.clearWindowS == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'reversal requires clearWindowS',
        path: ['clearWindowS'],
      });
    }
    if (seg.kind === 'collapse' && seg.collapseSpeed == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'collapse requires collapseSpeed',
        path: ['collapseSpeed'],
      });
    }
  });

const HEX = z.string().regex(/^#[0-9a-fA-F]{3,8}$/, 'hex color like #1a2b3c');

/**
 * A level's BIOME (design §20) — the environment skin: which named setting this level is in,
 * plus the resolved palette overrides so the level is self-contained for the game (no separate
 * registry to sync). `name` refs a biome defined in Art Direction / the previs-skin registry;
 * the colour fields are the resolved snapshot the design tool writes on assignment. Any unset
 * colour falls back to the game's default palette. Absent `biome` ⇒ the default look.
 */
export const BiomeSchema = z.object({
  name: z.string().min(1),
  sky: HEX.optional(),
  ground: HEX.optional(),
  groundEdge: HEX.optional(),
  fence: HEX.optional(),
  hazard: HEX.optional(),
  accent: HEX.optional(),
  /** Optional backdrop image ref under the built game's apps/display/public. */
  background: z.string().optional(),
});
export type Biome = z.infer<typeof BiomeSchema>;

/**
 * One escalating phase of an ENCOUNTER (design §22). The generic encounter system walks
 * `telegraph → attack → vulnerable → recover` off these timers; `attack` names the behaviour
 * that fires (the game maps it to a mechanic — a free string so the model stays game-agnostic).
 * A mini has one phase; a finale escalates over several.
 */
export const EncounterPhaseSchema = z.object({
  /** Wind-up tell (s) before the attack — the readable telegraph. */
  telegraphSec: z.number().finite().min(0),
  /** Attack duration (s) — the hazard is live and contact downs the runner. */
  attackSec: z.number().finite().positive(),
  /** Vulnerable duration (s) — the weak point is exposed; hit it in this window. */
  vulnerableSec: z.number().finite().min(0),
  /** Recovery (s) before the next telegraph. */
  recoverSec: z.number().finite().min(0),
  /** Which behaviour fires — a game-interpreted id (e.g. "slam", "geyserBurst"). */
  attack: z.string().min(1),
  /** Weak-point height above ground (m) during the vulnerable window (the hit target). */
  weakPointHeight: z.number().finite().min(0).optional(),
});
export type EncounterPhase = z.infer<typeof EncounterPhaseSchema>;

/**
 * An ENCOUNTER (design §22) — a first-class boss / set-piece ARENA, the answer to "a boss must
 * be a designed fight, not an entity you auto-run into". `locomotion` says what the constant
 * forward run does inside the arena (`halt` = stop and fight; `loop` = pace/circulate); `ref`
 * names the boss/set-piece Content item (resolves against the palette); `phases` are the
 * escalating attack pattern. Generic across games — a game reads it to script the fight.
 */
export const EncounterSchema = z.object({
  /** Content item name for the boss / set-piece (exact-name resolve, LEVELS-README #3). */
  ref: z.string().min(1),
  /** Severity tier — a mini-boss beat vs a world finale. Absent = a generic set-piece. */
  tier: z.enum(['miniboss', 'boss']).optional(),
  /** What the forward run does in the arena. Default `halt` (stop and fight). */
  locomotion: z.enum(['halt', 'loop']).default('halt'),
  /**
   * How the player DAMAGES the boss — the encounter's core verb (design §22).
   * `stop-block-timed` (preferred): a Stop Block sits in front of the boss; the player TIMES a
   * jump off it to strike the weak point during the vulnerable window, and a bad hit bounces
   * them back onto the block to retry — a rhythm duel, NOT running into the boss's face.
   * `direct` = collide/stomp with no staging (legacy; reads as "run into the caterpillar").
   */
  approach: z.enum(['stop-block-timed', 'direct']).default('stop-block-timed'),
  /** Hits to defeat. Absent ⇒ one hit per phase. */
  hits: z.number().int().positive().optional(),
  /** Fixed arena length (m) the fight occupies at the end of the track. */
  arenaSpan: z.number().finite().positive().optional(),
  /** Ordered attack phases (mini = 1; finale escalates). */
  phases: z.array(EncounterPhaseSchema).min(1),
});
export type Encounter = z.infer<typeof EncounterSchema>;

export const SpatialLevelSchema = z
  .object({
    archetype: z.literal('autorunner-2d'),
    targetSeconds: z.number().finite().positive(),
    vertices: z.array(VertexSchema).min(1),
    segments: z.array(SegmentSchema).optional(),
    entities: z.array(SpatialEntitySchema).optional(),
    terraces: z.array(TerraceSchema).optional(),
    /** Environment skin (design §20) — snowy, cave, etc. Absent = default look. */
    biome: BiomeSchema.optional(),
    /** Boss / set-piece arena (design §22). Absent = a plain run. A boss-tagged level with
     *  no encounter is flagged so the runner never just collides with the boss. */
    encounter: EncounterSchema.optional(),
  })
  .superRefine((lvl, ctx) => {
    const entityIds = new Set<string>();
    (lvl.entities ?? []).forEach((e, i) => {
      if (entityIds.has(e.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate entity id: ${e.id}`,
          path: ['entities', i, 'id'],
        });
      }
      entityIds.add(e.id);
    });
    const terraceIds = new Set<string>();
    (lvl.terraces ?? []).forEach((ter, i) => {
      if (terraceIds.has(ter.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate terrace id: ${ter.id}`,
          path: ['terraces', i, 'id'],
        });
      }
      terraceIds.add(ter.id);
    });
    const seen = new Set<string>();
    let prevT = -Infinity;
    lvl.vertices.forEach((v, i) => {
      if (seen.has(v.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate vertex id: ${v.id}`,
          path: ['vertices', i, 'id'],
        });
      }
      seen.add(v.id);
      if (v.t <= prevT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'vertex t must be strictly increasing',
          path: ['vertices', i, 't'],
        });
      }
      prevT = v.t;
    });
    const idx = new Map(lvl.vertices.map((v, i) => [v.id, i]));
    (lvl.segments ?? []).forEach((s, i) => {
      const from = idx.get(s.fromId);
      const to = idx.get(s.toId);
      if (from === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `segment fromId not found: ${s.fromId}`,
          path: ['segments', i, 'fromId'],
        });
      }
      if (to === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `segment toId not found: ${s.toId}`,
          path: ['segments', i, 'toId'],
        });
      }
      if (from !== undefined && to !== undefined && to !== from + 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'segment must connect adjacent vertices',
          path: ['segments', i],
        });
      }
    });
  });

export type SpatialLevel = z.infer<typeof SpatialLevelSchema>;
